// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const MILESTONES = [
  { id:1, name:"VitaSync Beta Complete", project:"VitaSync Launch", dueDate:"2026-02-14", status:"On Track", owner:"Des Bachynski", dependencies:[], description:"Complete beta testing with 3 pilot clinics and compile feedback report." },
  { id:2, name:"API v3 Specification Approved", project:"Platform API v3", dueDate:"2026-01-31", status:"Completed", owner:"Jordan Lutz", dependencies:[], description:"Final approval of API v3 specification document by technical review board." },
  { id:3, name:"HVAC Module Complete", project:"Vantage Ops Verticals", dueDate:"2026-02-10", status:"Completed", owner:"Jordan Lutz", dependencies:[], description:"Complete HVAC workflow module with scheduling, dispatch, and compliance tracking." },
  { id:4, name:"Canstorr Hardware Ordered", project:"Canstorr Phase 2", dueDate:"2026-02-15", status:"Completed", owner:"Tyson Becker", dependencies:[], description:"Procurement of all sensor hardware for Phase 2 bin expansion." },
  { id:5, name:"GlowDesk Pages Built", project:"GlowDesk Recording", dueDate:"2026-02-20", status:"At Risk", owner:"Jordan Lutz", dependencies:[], description:"Complete all 3 recording pages: ClientConsultations, ESignature, Transcriptions." },
  { id:6, name:"VitaSync Compliance Approved", project:"VitaSync Launch", dueDate:"2026-02-28", status:"On Track", owner:"Des Bachynski", dependencies:["VitaSync Beta Complete"], description:"Provincial health compliance documentation approved by regulatory body." },
  { id:7, name:"Mining Requirements Gathered", project:"Mining Sector Expansion", dueDate:"2026-02-28", status:"At Risk", owner:"Lee Mehling", dependencies:[], description:"Complete requirements gathering from Western Mining Corp for 3 mine sites." },
  { id:8, name:"API v3 Core Features", project:"Platform API v3", dueDate:"2026-03-01", status:"On Track", owner:"Jordan Lutz", dependencies:["API v3 Specification Approved"], description:"Auth refactor, rate limiting, and real-time notifications complete." },
  { id:9, name:"All Vertical Modules Done", project:"Vantage Ops Verticals", dueDate:"2026-03-15", status:"On Track", owner:"Jordan Lutz", dependencies:["HVAC Module Complete"], description:"Plumbing and electrical workflow modules completed and tested." },
  { id:10, name:"VitaSync Launch Day", project:"VitaSync Launch", dueDate:"2026-03-15", status:"On Track", owner:"Des Bachynski", dependencies:["VitaSync Compliance Approved"], description:"Official launch of VitaSync platform to production." },
  { id:11, name:"Canstorr Installation Complete", project:"Canstorr Phase 2", dueDate:"2026-03-30", status:"On Track", owner:"Tyson Becker", dependencies:["Canstorr Hardware Ordered"], description:"All new sensors installed and calibrated at Wroxton facility." },
  { id:12, name:"Mining Module Development", project:"Mining Sector Expansion", dueDate:"2026-04-15", status:"On Track", owner:"Lee Mehling", dependencies:["Mining Requirements Gathered"], description:"Custom compliance and safety monitoring modules built for Western Mining." }
];

const PROJECTS = ["All", ...new Set(MILESTONES.map(m => m.project))];
const STATUS_OPTIONS = ["All","On Track","At Risk","Overdue","Completed"];

export default function MilestonesPage() {
  const [milestones] = useState(MILESTONES);
  const [projectFilter, setProjectFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = milestones.filter(m => {
    if (projectFilter !== "All" && m.project !== projectFilter) return false;
    if (statusFilter !== "All" && m.status !== statusFilter) return false;
    return true;
  });

  const onTrack = milestones.filter(m => m.status === "On Track").length;
  const atRisk = milestones.filter(m => m.status === "At Risk").length;
  const completed = milestones.filter(m => m.status === "Completed").length;

  const statusBadge = (status) => {
    const map = { "On Track":"badge badge-green", "At Risk":"badge badge-yellow", Overdue:"badge badge-red", Completed:"badge badge-blue" };
    return map[status] || "badge";
  };

  const months = ["Feb 2026","Mar 2026","Apr 2026"];
  const monthRanges = [
    { start:"2026-02-01", end:"2026-02-28" },
    { start:"2026-03-01", end:"2026-03-31" },
    { start:"2026-04-01", end:"2026-04-30" }
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Milestones</h1><p>Cross-project milestone tracking</p></div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Milestones</div><div className="stat-value">{milestones.length}</div></div>
        <div className="stat-card"><div className="stat-label">On Track</div><div className="stat-value" style={{color:"#10b981"}}>{onTrack}</div></div>
        <div className="stat-card"><div className="stat-label">At Risk</div><div className="stat-value" style={{color:"#eab308"}}>{atRisk}</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value" style={{color:"#3b82f6"}}>{completed}</div></div>
      </div>

      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <select className="input" value={projectFilter} onChange={e => setProjectFilter(e.target.value)} style={{width:250}}>
          {PROJECTS.map(p => <option key={p} value={p}>{p === "All" ? "All Projects" : p}</option>)}
        </select>
        <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{width:180}}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>)}
        </select>
      </div>

      {/* Timeline View */}
      <div className="card">
        <div className="card-header"><h3>Timeline</h3></div>
        {months.map((month, mi) => {
          const monthMilestones = filtered.filter(m => m.dueDate >= monthRanges[mi].start && m.dueDate <= monthRanges[mi].end);
          if (monthMilestones.length === 0) return null;
          return (
            <div key={month} style={{marginBottom:20}}>
              <div style={{fontSize:14,fontWeight:700,color:"#94a3b8",marginBottom:10,paddingBottom:6,borderBottom:"1px solid #334155"}}>{month}</div>
              {monthMilestones.sort((a,b) => a.dueDate.localeCompare(b.dueDate)).map(m => (
                <div key={m.id} style={{display:"flex",alignItems:"flex-start",gap:16,padding:"10px 0",borderBottom:"1px solid #1e293b",marginLeft:16}}>
                  <div style={{width:4,minHeight:40,borderRadius:2,background:m.status==="Completed"?"#3b82f6":m.status==="At Risk"?"#eab308":"#10b981",marginTop:4}} />
                  <div style={{width:70,flexShrink:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#e2e8f0"}}>{m.dueDate.split("-")[2]}</div>
                    <div style={{fontSize:10,color:"#64748b"}}>{month.split(" ")[0]}</div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:14,fontWeight:600,color:"#e2e8f0"}}>{m.name}</span>
                      <span className={statusBadge(m.status)}>{m.status}</span>
                    </div>
                    <div style={{fontSize:12,color:"#64748b",marginBottom:2}}>{m.project}</div>
                    <div style={{fontSize:12,color:"#94a3b8"}}>{m.description}</div>
                    <div style={{fontSize:11,color:"#64748b",marginTop:4}}>
                      Owner: {m.owner}
                      {m.dependencies.length > 0 && <span> | Depends on: {m.dependencies.join(", ")}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Milestone List */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>All Milestones</h3></div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{borderBottom:"2px solid #334155"}}>
              {["Milestone","Project","Due Date","Status","Owner","Dependencies"].map(h => (
                <th key={h} style={{textAlign:"left",padding:"10px 8px",fontSize:12,fontWeight:700,color:"#94a3b8"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.sort((a,b) => a.dueDate.localeCompare(b.dueDate)).map(m => (
              <tr key={m.id} style={{borderBottom:"1px solid #334155"}}>
                <td style={{padding:"10px 8px",fontSize:13,fontWeight:600,color:"#e2e8f0"}}>{m.name}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#94a3b8"}}>{m.project}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#e2e8f0"}}>{m.dueDate}</td>
                <td style={{padding:"10px 8px"}}><span className={statusBadge(m.status)}>{m.status}</span></td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#e2e8f0"}}>{m.owner}</td>
                <td style={{padding:"10px 8px",fontSize:11,color:"#64748b"}}>{m.dependencies.length > 0 ? m.dependencies.join(", ") : "None"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid-2" style={{marginTop:16}}>
        <div className="card">
          <div className="card-header"><h3>By Project</h3></div>
          {[...new Set(milestones.map(m => m.project))].map(proj => {
            const count = milestones.filter(m => m.project === proj).length;
            const done = milestones.filter(m => m.project === proj && m.status === "Completed").length;
            return (
              <div key={proj} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #334155"}}>
                <span style={{fontSize:13,color:"#e2e8f0"}}>{proj}</span>
                <span style={{fontSize:12,color:"#94a3b8"}}>{done}/{count} done</span>
              </div>
            );
          })}
        </div>
        <div className="card">
          <div className="card-header"><h3>By Owner</h3></div>
          {[...new Set(milestones.map(m => m.owner))].map(owner => {
            const count = milestones.filter(m => m.owner === owner).length;
            return (
              <div key={owner} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #334155"}}>
                <span style={{fontSize:13,color:"#e2e8f0"}}>{owner}</span>
                <span style={{fontSize:13,fontWeight:600,color:"#10b981"}}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
